/* 🔴 W136 B2 — A DISTANCE, IN THE COACH'S UNIT (Fable's design D6, 2026-10-05, docs/W136-DISTANCE-ROUNDS.md).
 *
 * His words (2026-10-02): *"i think distance only is not shown as km or m as it should"*.
 *
 *   · STORED IN METRES, ALWAYS (`coach_workout_sets.distance_m`, `set_logs.distance_m`, numeric(8,2)).
 *   · The coach's FIELD picks the unit — `distance_m` → metres, `distance_km` → kilometres — never the magnitude: 5,000 m on
 *     a metres row stays `5000`, and 400 m on a kilometres row reads `0.4`.
 *   · A km cell shows AT MOST TWO DECIMALS and accepts `,` as well as `.` (a distance is typed the way the coach writes it;
 *     his W46 dot-only rule is about KG and stays there).
 *   · ⚠️ A FOCUS-AND-BLUR WITH NO EDIT WRITES NOTHING. 21,097.5 m on a km row shows `21.1`; reading that text back would
 *     store 21,100 — the tempo-normaliser trap (`TRAP-CODEX` live-database-discipline). So the cell COMMITS only from a
 *     keystroke it accepted (`distanceChange`), and blur only redraws the text from the model (`distanceText`) — there is
 *     no path from a blur to a write. `distanceCell.test.ts` executes the sequence.
 */
import type { TrackingField } from "./builder";

export type DistanceUnit = "m" | "km";

/** The cap the log's CHECK holds (`set_logs.distance_m <= 100000`) — 100 km, the most a cell may say. */
export const MAX_DISTANCE_M = 100_000;

/** The unit a column types in, or null for a column that is not a distance. */
export function distanceUnitOf(column: TrackingField): DistanceUnit | null {
  return column === "distance_m" ? "m" : column === "distance_km" ? "km" : null;
}

/** The unit an exercise's fields name — the first distance field — or metres, the stored unit, when they name none. */
export function distanceUnitFor(fields: readonly string[]): DistanceUnit {
  for (const f of fields) {
    if (f === "distance_m") return "m";
    if (f === "distance_km") return "km";
  }
  return "m";
}

/** Two decimals at most, trailing zeros dropped: 21097.5 → `21097.5` (m) / `21.1` (km); 5000 → `5000` / `5`. */
function trim(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/** What a cell shows for a stored distance — empty when nothing is prescribed (an absent value is not a value). */
export function distanceText(metres: number | null, unit: DistanceUnit): string {
  if (metres === null) return "";
  return trim(unit === "km" ? metres / 1000 : metres);
}

/** May the cell hold this text — digits with one optional `.` or `,` and at most two decimals, never past the cap. */
export function acceptsDistance(text: string, unit: DistanceUnit): boolean {
  if (text === "") return true;
  if (!/^\d{1,6}([.,]\d{0,2})?$/.test(text)) return false;
  const n = Number(text.replace(",", "."));
  return (unit === "km" ? n * 1000 : n) <= MAX_DISTANCE_M;
}

/** The metres a COMPLETE text means; null for empty, a half-typed `2.`, or zero (a zero distance is no prescription —
 *  the log's CHECK refuses it). Kilometres are converted and kept to the column's two decimals. */
export function distanceMetres(text: string, unit: DistanceUnit): number | null {
  if (text === "" || !acceptsDistance(text, unit) || /[.,]$/.test(text)) return null;
  const n = Number(text.replace(",", "."));
  if (!(n > 0)) return null;
  return Math.round((unit === "km" ? n * 1000 : n) * 100) / 100;
}

/** One keystroke: the text to keep and the metres to commit — or `null` when the keystroke is refused (revert it).
 *  ⚠️ This is the ONLY function that yields a commit; blur has none by construction. */
export function distanceChange(next: string, unit: DistanceUnit): { text: string; commit: number | null } | null {
  if (!acceptsDistance(next, unit)) return null;
  return { text: next, commit: distanceMetres(next, unit) };
}

/** The words on screen (design §5): `400 m`, `5 km`, `21.1 km`. */
export function formatDistance(metres: number, unit: DistanceUnit): string {
  return `${distanceText(metres, unit)} ${unit}`;
}
